import { Component } from '@theme/component';
import { NotifyMeAddedEvent, ThemeEvents, VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback, getViewParameterValue } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {HTMLElement | null} */
  #loaderElement = null;

  /** @type {HTMLElement[]} */
  #loadingMediaGalleries = [];

  connectedCallback() {
    super.connectedCallback();
    // Ensure fieldsets is always an array (could be single element or array from refs)
    const fieldsetsRaw = this.refs.fieldsets;
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (
      Array.isArray(fieldsetsRaw)
        ? fieldsetsRaw
        : fieldsetsRaw
          ? [fieldsetsRaw]
          : []
    );

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));

    document.addEventListener(ThemeEvents.notifyMeAdded, this.#handleNotifyMeAdded);

    this.multi_option = this.querySelector("product-multi-option") ? true : false;
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {

    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
        ? 'featured-product-information'
        : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    const url = new URL(window.location.href);

    let variantId = selectedOption.dataset.variantId || null;
    if (event.target.hasAttribute('data-multi-option')) {
      const reverseMap = Object.fromEntries(
        Object.entries(this.data.optionsMap).map(([key, value]) => [value, key])
      );

      const option = this.selectedOptionsValues.map(value => reverseMap[value]).join(' / ');

      variantId = this.data.variants.find(variant => variant.option === option)?.id;
    }

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        // Ensure fieldsets is always an array (could be single element or array from refs)
        const fieldsetsRaw = this.refs.fieldsets;
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (
          Array.isArray(fieldsetsRaw)
            ? fieldsetsRaw
            : fieldsetsRaw
              ? [fieldsetsRaw]
              : []
        );
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
            fieldset.style.setProperty(
              '--pill-width-current',
              `${radios[newCurrentIndex].parentElement?.offsetWidth || 0}px`
            );
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
            fieldset.style.setProperty(
              '--pill-width-previous',
              `${radios[newPreviousIndex].parentElement?.offsetWidth || 0}px`
            );
          }
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'swatches-default-variant-picker-component': 'section-rendering-product-default-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    // Determine the loader strategy based on context:
    // - PLP swatches (inside product cards): no loader
    // - PDP (main product page, not in dialogs/cards): targeted image-level skeleton loader
    // - Other contexts (quick-add, cart drawer, featured product): full-page loader
    const isInPLPSwatch = this.closest('product-card') || this.closest('product-default-card');
    const isInQuickAdd = this.closest('quick-add-dialog');
    const isInCartDrawer = this.closest('cart-drawer');
    const isInFeaturedProduct = this.closest('featured-product-information');
    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !isInPLPSwatch &&
      !isInQuickAdd &&
      !isInCartDrawer &&
      !isInFeaturedProduct;

    if (isOnProductPage) {
      this.showImageLoader();
    } else if (!isInPLPSwatch) {
      this.showLoader();
    }

    const hideActiveLoader = () => {
      if (isOnProductPage) {
        this.hideImageLoader();
      } else if (!isInPLPSwatch) {
        this.hideLoader();
      }
    };

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        // Look for the JSON script tag within any variant picker component
        const textContent = html.querySelector(`${this.tagName.toLowerCase()} script[type="application/json"]`)?.textContent;
        if (!textContent) {
          hideActiveLoader();
          return;
        }

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          let newProduct;
          const variantData = JSON.parse(textContent);
          if (!this.multi_option) {
            newProduct = this.updateVariantPicker(html);
          } else {
            newProduct = undefined;
            // For multi-option: update the form's active variant data attributes
            const form = this.querySelector('.variant-picker__form');
            if (form instanceof HTMLElement && variantData) {
              form.dataset.activeVariantSku = variantData.sku ?? '';
              form.dataset.activeVariantId = String(variantData.id ?? '');
            }
          }
          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(variantData, this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }

        hideActiveLoader();
      })
      .catch((error) => {
        hideActiveLoader();

        // Ignore AbortError as it's expected when a new request cancels the previous one
        if (error.name !== 'AbortError') {
          console.error('Error fetching section:', error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    const preservedSliders = [...element.querySelectorAll('customer-story-slider')].map((slider) => {
      const block = slider.closest('.shopify-block');
      const host = block || slider;
      return {
        slider,
        block,
        parent: host.parentElement,
        nextSibling: host.nextSibling,
      };
    });

    this.#preserveCustomerStorySliders(element, newElement);
    morph(element, newElement);
    this.#restoreCustomerStorySliders(element, preservedSliders);
    this.#rerenderCustomerStorySliders(element);
  }

  /**
   * Keeps live customer-story-slider nodes through a featured-product morph.
   * The Section Rendering API response often omits the metaobject video list,
   * so the new HTML has no slider and morph would delete the existing one.
   * @param {Element} liveRoot
   * @param {Element} newRoot
   */
  #preserveCustomerStorySliders(liveRoot, newRoot) {
    liveRoot.querySelectorAll('customer-story-slider').forEach((liveSlider) => {
      liveSlider.setAttribute('data-skip-subtree-update', '');

      const blockId = liveSlider.getAttribute('data-block-id');
      const newSlider = blockId
        ? newRoot.querySelector(`customer-story-slider[data-block-id="${CSS.escape(blockId)}"]`)
        : newRoot.querySelector('customer-story-slider');

      if (newSlider instanceof Element) {
        newSlider.setAttribute('data-skip-subtree-update', '');
        return;
      }

      const liveBlock = liveSlider.closest('.shopify-block');
      const newBlock = liveBlock?.id ? newRoot.querySelector(`#${CSS.escape(liveBlock.id)}`) : null;

      if (newBlock instanceof Element && liveBlock instanceof Element) {
        liveBlock.setAttribute('data-skip-subtree-update', '');
        newBlock.setAttribute('data-skip-subtree-update', '');
        return;
      }

      const details =
        newRoot.querySelector('[data-id="product-information-details"] .group-block-content') ||
        newRoot.querySelector('.product-details');
      if (!details) return;

      const source = liveBlock || liveSlider;
      source.setAttribute('data-skip-subtree-update', '');
      const clone = source.cloneNode(true);
      if (!(clone instanceof Element)) return;

      clone.setAttribute('data-skip-subtree-update', '');
      const clonedSlider = clone.matches('customer-story-slider')
        ? clone
        : clone.querySelector('customer-story-slider');
      clonedSlider?.setAttribute('data-skip-subtree-update', '');
      details.appendChild(clone);
    });
  }

  /**
   * Re-inserts sliders that morph removed because they were missing from the
   * Section Rendering API HTML.
   * @param {Element} root
   * @param {{ slider: Element, block: Element | null, parent: Element | null, nextSibling: ChildNode | null }[]} preserved
   */
  #restoreCustomerStorySliders(root, preserved) {
    const fallbackParent =
      root.querySelector('[data-id="product-information-details"] .group-block-content') ||
      root.querySelector('.product-details') ||
      root;

    for (const { slider, block, parent, nextSibling } of preserved) {
      if (slider.isConnected) continue;

      const insertParent = (block?.isConnected ? block : null) || (parent?.isConnected ? parent : fallbackParent);

      if (block && !block.isConnected) {
        if (nextSibling?.parentNode === insertParent) {
          insertParent.insertBefore(block, nextSibling);
        } else {
          insertParent.appendChild(block);
        }
      }

      if (!slider.isConnected) {
        insertParent.appendChild(slider);
      }

      block?.classList.remove('hidden');
    }
  }

  /**
   * Rebuilds Swiper after the section morph mutates or reconnects the slider.
   * @param {Element} root
   */
  #rerenderCustomerStorySliders(root) {
    root.querySelectorAll('customer-story-slider').forEach((slider) => {
      if (typeof slider.initSwiper === 'function') {
        slider.initSwiper();
      }
    });
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');
    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    const is_multi_options = selectedOptions.some((option) => option.hasAttribute('data-multi-option'));

    if (is_multi_options) {
      this.data = this.querySelector("product-multi-option").data
      const getMultiOptions = selectedOptions.filter((option) => option.hasAttribute('data-multi-option'));

      const selectedOptionFromMultiOption = getMultiOptions.map((option) => option.value).join('/');
      let multiOptionId = this.data.optionsMap[selectedOptionFromMultiOption];

      if (!multiOptionId) {
        const matchedOption = this.possibleFirstMatchedMultiOptionID(getMultiOptions, selectedOptionFromMultiOption);
        multiOptionId = matchedOption.value;
      }
      const getNonMultiOptions = selectedOptions.filter((option) => !option.hasAttribute('data-multi-option'));
      const optionsIDS = getNonMultiOptions.map((option) => {
        const { optionValueId } = option.dataset;
        if (!optionValueId) throw new Error('No option value ID found');
        return optionValueId;
      });
      return [multiOptionId, ...optionsIDS];
    }
    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }


  /**
   * Gets the selected options values.
   * @param {HTMLElement[]} selectedMultOptions - The selected inputs.
   * @param { string } selectedOptionFromMultiOption - The selected option
   * @returns {string} First matched id from the group.
   */

  possibleFirstMatchedMultiOptionID(selectedMultOptions, selectedOptionFromMultiOption) {
    const options = this.data.optionsMap;
    const selectedParts = selectedOptionFromMultiOption.split('/');
    const entries = Object.entries(options);

    // Progressive fallback matching: try full match, then 0+1, then index 0
    for (let i = selectedParts.length; i > 0; i--) {
      const matchPrefix = selectedParts.slice(0, i).join('/');
      const match = entries.find(([key]) => key.startsWith(matchPrefix));
      if (match) return { key: match[0], value: match[1] };
    }

    // Fallback to first available option
    const [firstKey, firstValue] = entries[0] || ['', ''];
    return { key: firstKey, value: firstValue };
  }

  /**
   * Shows the page-level loader overlay.
   * Uses the existing loader snippet rendered in the DOM.
   */
  showLoader() {
    // Find the existing loader element from the snippet
    const loaderElement = document.querySelector('.loader');

    if (!(loaderElement instanceof HTMLElement)) {
      console.warn('Loader element not found. Ensure {% render "loader" %} is included in the template.');
      return;
    }

    // Check if loader is already showing (reference counting for multiple variant pickers)
    const currentCount = parseInt(loaderElement.dataset.refCount || '0', 10);
    const newCount = currentCount + 1;
    loaderElement.dataset.refCount = String(newCount);

    // Remove hidden class to show loader
    loaderElement.classList.remove('hidden');
    loaderElement.setAttribute('aria-hidden', 'false');
    loaderElement.setAttribute('aria-busy', 'true');

    this.#loaderElement = loaderElement;
  }

  /**
   * Shows targeted skeleton loaders on the product media gallery (PDP only).
   * Scopes to the closest shopify-section so it does not affect unrelated galleries.
   * Used instead of the full-page loader to avoid dimming the entire page.
   */
  showImageLoader() {
    const scope = this.closest('.shopify-section') || document;
    /** @type {NodeListOf<HTMLElement>} */
    const galleries = scope.querySelectorAll('media-gallery');

    if (galleries.length === 0) return;

    this.#loadingMediaGalleries = [];
    galleries.forEach((gallery) => {
      gallery.classList.add('is-variant-loading');
      gallery.setAttribute('aria-busy', 'true');
      this.#loadingMediaGalleries.push(gallery);
    });
  }

  /**
   * Removes the targeted skeleton loaders from the product media gallery.
   * Falls back to querying the current section if the cached reference is stale
   * (e.g. when the gallery was replaced by a variant update).
   */
  hideImageLoader() {
    const tracked = this.#loadingMediaGalleries.length
      ? this.#loadingMediaGalleries
      : Array.from(
          (this.closest('.shopify-section') || document).querySelectorAll('media-gallery')
        );

    tracked.forEach((gallery) => {
      if (!(gallery instanceof HTMLElement) || !gallery.isConnected) return;
      gallery.classList.remove('is-variant-loading');
      gallery.setAttribute('aria-busy', 'false');
    });

    // The new media-gallery (after variant morph) may not be in the tracked list,
    // so also clean up any galleries in the current section that still carry the class.
    const scope = this.closest('.shopify-section') || document;
    scope.querySelectorAll('media-gallery.is-variant-loading').forEach((gallery) => {
      gallery.classList.remove('is-variant-loading');
      gallery.setAttribute('aria-busy', 'false');
    });

    this.#loadingMediaGalleries = [];
  }

  /**
   * Hides the page-level loader overlay.
   * Uses reference counting to handle multiple simultaneous variant changes.
   */
  hideLoader() {
    const loaderElement = this.#loaderElement || document.querySelector('.loader');

    if (!(loaderElement instanceof HTMLElement)) {
      return;
    }

    // Decrement reference count
    const currentCount = parseInt(loaderElement.dataset.refCount || '1', 10);
    const newCount = Math.max(0, currentCount - 1);
    loaderElement.dataset.refCount = String(newCount);

    // Only hide loader if no other variant pickers are using it
    if (newCount === 0) {
      loaderElement.classList.add('hidden');
      loaderElement.setAttribute('aria-hidden', 'true');
      loaderElement.setAttribute('aria-busy', 'false');
    }

    this.#loaderElement = null;
  }

  /**
   * Handles the notify me added event.
   * @param {NotifyMeAddedEvent} event - The notify me added event.
   */
  #handleNotifyMeAdded(event) {
    const _this = this.querySelector('variant-picker');
    if (!_this) return;
    const { resource } = event.detail;
    const { sku } = resource;
    
    // Clear is-notified from ALL bell icons first
    this.querySelectorAll('.variant-option__button-label__icon.is-notified').forEach((el) => {
      el.classList.remove('is-notified');
    });
    
    const selectedOptions = Array.from(_this.querySelectorAll('select option[selected], fieldset input:checked'));
    const activeVariantSku = _this.querySelector('.variant-picker__form')?.dataset.activeVariantSku;

    if (sku === activeVariantSku) {
      return selectedOptions.map((option) => {
        if (option.hasAttribute("aria-disabled")) {
          option.parentElement?.nextElementSibling?.classList.add("is-notified")
        }
      });
    }
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
